/**
 * ReportBody — a research run's report rendered as markdown + sources.
 *
 * Shared by both surfaces (DEEP_RESEARCH_REDESIGN.md: one implementation):
 *  • the agent chat card's sheet, inside ResearchRunView's Report tab
 *  • the research detail page, inline (no tabs, no sheet)
 *
 * Owns the sources ref so [S#] citations scroll to the evidence; the host
 * decides what [F#] does via `onOpenFinding` (switch tab vs scroll to the
 * findings block).
 */

import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ReportMarkdown } from '@/components/research/ReportMarkdown'
import { EvidenceCard } from '@/components/search/detail/EvidenceCard'
import type { HypothesisRun } from '@/types/hypothesis'

export interface ReportBodyProps {
  run: HypothesisRun
  findingCount: number
  /** Invoked when a [F#] citation is clicked. */
  onOpenFinding?: () => void
  /** Entity drill-in override; omit to let EvidenceCard open its own sheet. */
  onOpenEntity?: (nodeId: string) => void
}

export function ReportBody({ run, findingCount, onOpenFinding, onOpenEntity }: ReportBodyProps) {
  const { t } = useTranslation()
  const sourcesRef = useRef<HTMLDivElement>(null)
  const sources = run.sources ?? []

  return (
    <div className="flex flex-col gap-4">
      {run.reportMd ? (
        <ReportMarkdown
          markdown={run.reportMd}
          findingCount={findingCount}
          sourceCount={sources.length}
          onOpenFinding={onOpenFinding}
          onOpenSource={() => sourcesRef.current?.scrollIntoView({ behavior: 'smooth' })}
          onOpenEntityRef={onOpenEntity}
        />
      ) : run.summary ? (
        <p className="whitespace-pre-line break-words text-sm leading-relaxed text-foreground">
          {run.summary}
        </p>
      ) : (
        <p className="text-sm italic text-muted-foreground">{t('researchRun.noReport')}</p>
      )}
      {sources.length > 0 && (
        <div ref={sourcesRef}>
          <EvidenceCard evidence={sources} onOpenEntity={onOpenEntity} />
        </div>
      )}
    </div>
  )
}
